import React, { useState, useEffect } from "react";
import { fetchOneWithPopulates } from "../utils/fetchResources";
import { Link, useParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import {
	makeStyles,
	Typography,
	Button,
	List,
	IconButton,
	Tooltip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	TextField,
	useTheme,
} from "@material-ui/core";
import { DragDropContext, Droppable } from "react-beautiful-dnd";
import Column from "../components/Column";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";

function MenusEdit(props) {
	const theme = useTheme();
	const { id } = useParams();
	const [loading, setLoading] = useState(true);
	const [menu, setMenu] = useState();
	const [dndData, setDndData] = useState();
	const { enqueueSnackbar } = useSnackbar();
	const [openMainAdd, setOpenMainAdd] = useState(false);
	const [newMainLink, setNewMainLink] = useState();

	useEffect(() => {
		(async () => {
			await fetchOneWithPopulates("menus", id, setMenu);
			setLoading(false);
		})();
	}, [id]);

	//This entire useEffect hook is used to reformat the JSON data received from the API into a format that is useable by the react-beautiful-dnd package. It will have to be reformatted again on PUT when running your fetch requests
	useEffect(() => {
		if (menu) {
			//Get a list of all dropdown links as an object of objects
			let allDropdownLinks = [];
			menu.links.forEach((mainLink, i) => {
				mainLink.children.forEach((childLink, i) => {
					allDropdownLinks = [
						...allDropdownLinks,
						{
							[childLink.title]: { title: childLink.title, url: childLink.url, isSubLink: childLink.isSubLink },
						},
					];
				});
			});
			setDndData((dndData) => {
				return {
					...dndData,
					//Convert array of objects to an object of objects with Object.assign
					childLinks: Object.assign({}, ...allDropdownLinks),
				};
			});

			//Get a list of all columns as an object of objects
			let allMainLinks = [];
			menu.links.forEach((mainLink, i) => {
				const allChildLinks = mainLink.children.map((child, i) => {
					return child.title;
				});
				allMainLinks = [
					...allMainLinks,
					{
						[mainLink.title]: {
							title: mainLink.title,
							url: mainLink.url,
							childLinkTitles: allChildLinks,
						},
					},
				];
			});
			setDndData((dndData) => {
				//Convert array of objects to an object of objects with Object.assign
				return { ...dndData, mainLinks: Object.assign({}, ...allMainLinks) };
			});

			//Get a list of all columns for ordering in an array
			//Like ['mainlink-1', 'mainlink-2']
			setDndData((dndData) => {
				return {
					...dndData,
					mainLinkOrder: menu.links.map((mainLink, i) => {
						return mainLink.title;
					}),
				};
			});
		}
	}, [menu]);

	const onSubmit = (e) => {
		e.preventDefault();

		console.log(dndData);

		// Reformat the JSON from react-beautiful-dnd format to the custom Express API format
		const reformattedMainLinks = dndData.mainLinkOrder.map((key) => {
			//Loop over the dndData.mainLinks object of objects
			//Use the dndData.mainLinkOrder for retaining order
			return dndData.mainLinks[key];
		});

		const withChildren = reformattedMainLinks.map((mainLink) => {
			const childLinks = mainLink.childLinkTitles.map((key) => {
				//Loop over the dndData.childLinks object of objects
				//Use the dndData.mainLinks[TITLE].childLinkTitles array for retaining order
				return dndData.childLinks[key];
			});

			return { title: mainLink.title, url: mainLink.url, children: childLinks };
		});

		//Add reformatted array of objects to an object with a key of 'links'
		const formatted = { links: [...withChildren] };

		fetch(`${process.env.REACT_APP_API_URL}/menus/${id}`, {
			method: "PUT",
			credentials: "include",
			body: JSON.stringify(formatted),
			headers: { "Content-Type": "application/json" },
		})
			.then((res) => res.json())
			.then((data) => {
				if (data.success) {
					enqueueSnackbar("Menu updated successfully!", { variant: "success" });
					const getUpdatedMenu = async () => {
						await fetchOneWithPopulates("menus", id, setMenu);
					};
					getUpdatedMenu();
				} else {
					const errorArray = data.error.split(",");
					errorArray.forEach((error) => {
						enqueueSnackbar(`${error}`, { variant: "error" });
					});
				}
			});
	};

	const useStyles = makeStyles((theme) => ({
		submit: {
			margin: theme.spacing(2, 0, 1),
		},
		mainList: {
			background: "none",
			padding: 0,
		},
		addMainLinkSection: {
			width: "100%",
			display: "flex",
			justifyContent: "center",
		},
	}));

	const classes = useStyles();

	const onDragEnd = (result) => {
		const { destination, source, draggableId, type } = result;

		//If item is dropped outside any droppable area, don't do anything
		if (!destination) {
			return;
		}

		//If item is dropped in the same position as it started, don't do anything
		if (
			destination.droppableId === source.droppableId &&
			destination.index === source.index
		) {
			return;
		}

		if (type === "mainLink") {
			const newMainLinkOrder = Array.from(dndData.mainLinkOrder);
			newMainLinkOrder.splice(source.index, 1);
			newMainLinkOrder.splice(destination.index, 0, draggableId);

			const newMainLinkOrderState = {
				...dndData,
				mainLinkOrder: newMainLinkOrder,
			};

			setDndData(newMainLinkOrderState);
			return;
		}

		// Get the source and destination mainLink
		const mainLinkStart = dndData.mainLinks[source.droppableId];
		const mainLinkEnd = dndData.mainLinks[destination.droppableId];

		//Moving a dropdown link within the same mainLink
		if (mainLinkStart === mainLinkEnd) {
			const newChildLinkTitles = Array.from(mainLinkStart.childLinkTitles);
			newChildLinkTitles.splice(source.index, 1);
			newChildLinkTitles.splice(destination.index, 0, draggableId);

			const newMainLink = {
				...mainLinkStart,
				childLinkTitles: newChildLinkTitles,
			};

			const newMainLinkState = {
				...dndData,
				mainLinks: {
					...dndData.mainLinks,
					[newMainLink.title]: newMainLink,
				},
			};

			// Set the state with the new order of child links
			setDndData(newMainLinkState);
			return;
		}

		//Moving a dropdown link from one mainLink to another
		const startChildLinkTitles = Array.from(mainLinkStart.childLinkTitles);
		startChildLinkTitles.splice(source.index, 1);
		const newMainLinkStart = {
			...mainLinkStart,
			childLinkTitles: startChildLinkTitles,
		};
		const endChildLinkTitles = Array.from(mainLinkEnd.childLinkTitles);
		endChildLinkTitles.splice(destination.index, 0, draggableId);
		const newMainLinkEnd = {
			...mainLinkEnd,
			childLinkTitles: endChildLinkTitles,
		};

		const newMainLinkState = {
			...dndData,
			mainLinks: {
				...dndData.mainLinks,
				[newMainLinkStart.title]: newMainLinkStart,
				[newMainLinkEnd.title]: newMainLinkEnd,
			},
		};

		setDndData(newMainLinkState);
	};

	//Open add main link modal
	const handleClickOpenMainLinkAdd = () => {
		setOpenMainAdd(true);
	};

	const handleCloseMainLinkAdd = () => {
		setOpenMainAdd(false);
	};

	const onChange = (e) => {
		setNewMainLink({ ...newMainLink, [e.target.name]: e.target.value });
	};

	//Add new main link to the state
	const addMainLinkToState = (e) => {
		e.preventDefault();

		const newMainLinks = {
			...dndData.mainLinks,
			[newMainLink.title]: {
				title: newMainLink.title,
				url: newMainLink.url,
				childLinkTitles: [],
			},
		};

		const newMainLinksOrder = dndData.mainLinkOrder;
		newMainLinksOrder.push(newMainLink.title);

		const newDndState = {
			...dndData,
			mainLinks: newMainLinks,
			mainLinkOrder: newMainLinksOrder,
		};
		setDndData(newDndState);

		setOpenMainAdd(false);
	};

	if (loading) {
		return <div>Loading...</div>;
	}

	return (
		<>
			<Typography component="h1" variant="h4">
				Edit Menu:{" "}
				<Link
					style={{ color: theme.palette.primary.main }}
					to={`/${menu.site.slug}`}
					target="_blank"
				>
					{menu.site.name}
				</Link>
			</Typography>
			<DragDropContext onDragEnd={onDragEnd}>
				<Droppable droppableId="all-mainLinks" type="mainLink">
					{(provided) => (
						<List
							{...provided.droppableProps}
							innerRef={provided.innerRef}
							className={classes.mainList}
						>
							{dndData.mainLinkOrder.map((columnId, i) => {
								const column = dndData.mainLinks[columnId];
								const items = column.childLinkTitles.map((itemId) => {
									return dndData.childLinks[itemId];
								});
								return (
									<Column
										key={i}
										index={i}
										column={column}
										columns={dndData.mainLinks}
										items={items}
										setDndData={setDndData}
										dndData={dndData}
									/>
								);
							})}
							{provided.placeholder}
						</List>
					)}
				</Droppable>
			</DragDropContext>
			<div className={classes.addMainLinkSection}>
				<Tooltip
					title="Add Main Link"
					aria-label="add main link"
					placement="right"
				>
					<IconButton onClick={handleClickOpenMainLinkAdd}>
						<AddCircleOutlineIcon color="primary" fontSize="large" />
					</IconButton>
				</Tooltip>
				<Dialog
					open={openMainAdd}
					onClose={handleCloseMainLinkAdd}
					aria-labelledby="form-dialog-title"
				>
					<form onSubmit={addMainLinkToState}>
						<DialogTitle id="form-dialog-title">Add New Main Link</DialogTitle>
						<DialogContent>
							<DialogContentText>
								Please add a link title and a link URL
							</DialogContentText>
							<TextField
								autoFocus
								margin="dense"
								id="newMainLinkTitle"
								onChange={onChange}
								label="Title"
								type="text"
								name="title"
								fullWidth
								required
							/>
							<TextField
								margin="dense"
								id="newMainLinkURL"
								onChange={onChange}
								label="URL"
								type="text"
								name="url"
								fullWidth
								required
							/>
						</DialogContent>
						<DialogActions>
							<Button onClick={handleCloseMainLinkAdd} color="primary">
								Cancel
							</Button>
							<Button type="submit" color="primary">
								Add
							</Button>
						</DialogActions>
					</form>
				</Dialog>
			</div>
			<Button
				onClick={onSubmit}
				fullWidth
				variant="contained"
				color="primary"
				className={classes.submit}
			>
				Save
			</Button>
		</>
	);
}

export default MenusEdit;
